/**
 * Miracle Air OS | Rates & Taxes Service Layer
 * Production-Ready Backend Integration
 * 
 * This service now uses the backend API instead of localStorage.
 * All CRUD operations are persisted to PostgreSQL via the Fastify backend.
 */

import { Airstrip, AirstripDistance, ScheduledRoute, Leg, AdminSettings, RateAudit } from '../types';
import api from './api';

export interface SectorCommercials {
  usd: number;
  tzs: number;
  breakdown: {
    portFees: {
      landing: number;
      handling: number;
      navigation: number;
    };
    taxes: number;
    perPaxDepartureTax?: number;
    aircraftTax?: number;
    safetySecurity?: number;
    perPaxSafetySecurity?: number;
    ancillaries: {
      meetAndAssist: number;
      lounge: number;
    };
  };
  isInternational?: boolean;
  currency: 'USD' | 'TZS';
  fxRate: number;
  fxTimestamp: string;
}

class RatesService {
  // ============================================================================
  // AIRSTRIP CRUD OPERATIONS
  // ============================================================================

  /**
   * Retrieve all airstrips from the backend
   */
  async getAllAirstrips(): Promise<Airstrip[]> {
    try {
      const response = await api.get('/rates/airstrips');
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to fetch airstrips:', error);
      return [];
    }
  }

  /**
   * Get a single airstrip by code
   */
  async getAirstripByCode(code: string): Promise<Airstrip | null> {
    try {
      const response = await api.get(`/rates/airstrips/${code}`);
      return response.data?.data || null;
    } catch (error: any) {
      // A missing airstrip code is expected in some editing flows; return null quietly.
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch airstrip ${code}:`, error);
      }
      return null;
    }
  }

  /**
   * Create a new airstrip entry
   */
  async createAirstrip(
    airstrip: Omit<Airstrip, 'id' | 'lastUpdated' | 'createdAt' | 'updatedAt'>,
    userId: string,
    userName?: string
  ): Promise<Airstrip> {
    try {
      // Clean the payload - ensure all required fields are present and correct types
      const cleanAirstrip: any = {
        code: airstrip.code?.toUpperCase().trim(),
        name: airstrip.name?.trim(),
        country: airstrip.country?.trim(),
        latitude: Number(airstrip.latitude) || 0,
        longitude: Number(airstrip.longitude) || 0,
        landingFee: Number(airstrip.landingFee) || 0,
        handlingFee: Number(airstrip.handlingFee) || 0,
        internationalHandlingFee: Number((airstrip as any).internationalHandlingFee) || 0,
        navigationFee: Number(airstrip.navigationFee) || 0,
        safetySecurityDomesticFee: Number(airstrip.safetySecurityDomesticFee) || 0,
        safetySecurityInternationalFee: Number(airstrip.safetySecurityInternationalFee) || 0,
        paxBusLuggageFee: Number(airstrip.paxBusLuggageFee) || 0,
        aircraftPermitFee: Number(airstrip.aircraftPermitFee) || 0,
        clearanceFee: Number(airstrip.clearanceFee) || 0,
        repositionFee: Number(airstrip.repositionFee) || 0,
        paxTax: Number(airstrip.paxTax) || 0,
        paxTaxInternational: Number(airstrip.paxTaxInternational) || 0,
        fuelAvailable: Boolean(airstrip.fuelAvailable || false),
        isBorderPost: Boolean(airstrip.isBorderPost || false),
      };

      if (airstrip.icao && airstrip.icao.trim()) cleanAirstrip.icao = airstrip.icao.toUpperCase().trim();
      if (airstrip.management && airstrip.management.trim()) cleanAirstrip.management = airstrip.management.trim();
      if (airstrip.gpsCode && airstrip.gpsCode.trim()) cleanAirstrip.gpsCode = airstrip.gpsCode.trim();
      if (airstrip.runway && airstrip.runway.trim()) cleanAirstrip.runway = airstrip.runway.trim();
      if (airstrip.runwayLengthM !== undefined && airstrip.runwayLengthM !== null) cleanAirstrip.runwayLengthM = Number(airstrip.runwayLengthM);
      if (airstrip.surfaceType && airstrip.surfaceType.trim()) cleanAirstrip.surfaceType = airstrip.surfaceType.trim();
      if (airstrip.elevationFt !== undefined && airstrip.elevationFt !== null) cleanAirstrip.elevationFt = Number(airstrip.elevationFt);
      if (airstrip.nmsDar && airstrip.nmsDar.trim()) cleanAirstrip.nmsDar = airstrip.nmsDar.trim();
      if (airstrip.brgDar && airstrip.brgDar.trim()) cleanAirstrip.brgDar = airstrip.brgDar.trim();
      if (airstrip.status && airstrip.status.trim()) cleanAirstrip.status = airstrip.status.trim();
      if (airstrip.timeZoneIndex && airstrip.timeZoneIndex.trim()) cleanAirstrip.timeZoneIndex = airstrip.timeZoneIndex.trim();
      
      // Add optional fields only if they have values
      if (airstrip.arrivalTax !== undefined && airstrip.arrivalTax !== null) {
        cleanAirstrip.arrivalTax = Number(airstrip.arrivalTax);
      }
      if (airstrip.meetAndAssistFee !== undefined && airstrip.meetAndAssistFee !== null) {
        cleanAirstrip.meetAndAssistFee = Number(airstrip.meetAndAssistFee);
      }
      if (airstrip.loungeFee !== undefined && airstrip.loungeFee !== null) {
        cleanAirstrip.loungeFee = Number(airstrip.loungeFee);
      }
      if (airstrip.fuelAvailable && airstrip.fuelPricePerLiter !== undefined && airstrip.fuelPricePerLiter !== null) {
        cleanAirstrip.fuelPricePerLiter = Number(airstrip.fuelPricePerLiter);
      }
      if (airstrip.effectiveDate) {
        cleanAirstrip.effectiveDate = airstrip.effectiveDate;
      }
      
      const response = await api.post('/rates/airstrips', cleanAirstrip);
      return response.data?.data;
    } catch (error: any) {
      console.error('Failed to create airstrip:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create airstrip';
      const errorDetails = error.response?.data?.errors;
      if (errorDetails) {
        console.error('Validation errors:', errorDetails);
        throw new Error(`${errorMessage}: ${JSON.stringify(errorDetails)}`);
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing airstrip with audit trail
   */
  async updateAirstrip(
    code: string,
    updates: Partial<Airstrip>,
    userId: string,
    userName?: string
  ): Promise<Airstrip> {
    try {
      // Clean the payload - remove undefined values and ensure correct types
      const cleanUpdates: any = {};
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        // Skip undefined, null, and empty strings (except for boolean false)
        if (value !== undefined && value !== null && value !== '') {
          // Convert numbers properly
        if (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '') {
          cleanUpdates[key] = Number(value);
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          cleanUpdates[key] = value;
          } else if (typeof value === 'string') {
            cleanUpdates[key] = value;
          }
        } else if (typeof value === 'boolean' && value === false) {
          // Include false booleans
          cleanUpdates[key] = false;
        }
      });
      
      // Remove code from updates (it's in the URL)
      delete cleanUpdates.code;
      delete cleanUpdates.id;
      delete cleanUpdates.lastUpdated;
      delete cleanUpdates.createdAt;
      delete cleanUpdates.updatedAt;
      
      const response = await api.patch(`/rates/airstrips/${code}`, cleanUpdates);
      return response.data?.data;
    } catch (error: any) {
      console.error(`Failed to update airstrip ${code}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to update airstrip';
      const errorDetails = error.response?.data?.errors;
      if (errorDetails) {
        console.error('Validation errors:', errorDetails);
        throw new Error(`${errorMessage}: ${JSON.stringify(errorDetails)}`);
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete an airstrip (hard delete)
   */
  async deleteAirstrip(code: string, userId: string, userName?: string): Promise<void> {
    try {
      await api.delete(`/rates/airstrips/${code}`);
    } catch (error: any) {
      console.error(`Failed to delete airstrip ${code}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to delete airstrip');
    }
  }

  /**
   * Delete all airstrips (admin only)
   */
  async deleteAllAirstrips(): Promise<{ count: number }> {
    try {
      const response = await api.delete('/rates/airstrips');
      return response.data || { count: 0 };
    } catch (error: any) {
      console.error('Failed to delete all airstrips:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete all airstrips');
    }
  }

  /**
   * Download CSV template for airstrip import
   */
  async getAirstripImportTemplate(): Promise<string> {
    try {
      const response = await api.get('/rates/airstrips/template', { responseType: 'text' });
      return response.data || '';
    } catch (error: any) {
      console.error('Failed to download airstrip template:', error);
      throw new Error(error.response?.data?.message || 'Failed to download airstrip template');
    }
  }

  /**
   * Import airstrips from CSV (admin only)
   */
  async importAirstripsCsv(csv: string): Promise<{
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  }> {
    try {
      const response = await api.post('/rates/airstrips/import', { csv });
      return response.data?.data;
    } catch (error: any) {
      console.error('Failed to import airstrips:', error);
      const errorMessage = error.response?.data?.message || 'Failed to import airstrips';
      const errorDetails = error.response?.data?.errors;
      if (errorDetails) {
        throw new Error(`${errorMessage}: ${JSON.stringify(errorDetails)}`);
      }
      throw new Error(errorMessage);
    }
  }

  // ============================================================================
  // AIRSTRIP DISTANCE OVERRIDES
  // ============================================================================

  async getAirstripDistances(): Promise<AirstripDistance[]> {
    try {
      const response = await api.get('/rates/airstrip-distances');
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to fetch airstrip distances:', error);
      return [];
    }
  }

  async bulkUpsertAirstripDistances(items: Array<{ fromCode: string; toCode: string; distanceMiles: number }>): Promise<{
    processed: number;
    skipped: number;
    errors: Array<{ row: number; message: string }>;
  }> {
    try {
      const response = await api.put('/rates/airstrip-distances', { items });
      return response.data?.summary || { processed: 0, skipped: 0, errors: [] };
    } catch (error: any) {
      console.error('Failed to import airstrip distances:', error);
      const errorMessage = error.response?.data?.message || 'Failed to import airstrip distances';
      throw new Error(errorMessage);
    }
  }

  async upsertAirstripDistance(
    fromCode: string,
    toCode: string,
    payload: {
      distanceMiles?: number;
      overrideSpeedKts?: number;
      overrideRatePerMileUSD?: number;
      overrideRatePerHourUSD?: number;
      overrideBlockMinutes?: number;
    }
  ): Promise<AirstripDistance> {
    try {
      const response = await api.patch(`/rates/airstrip-distances/${fromCode}/${toCode}`, payload);
      return response.data?.data;
    } catch (error: any) {
      console.error('Failed to update airstrip distance:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update airstrip distance';
      throw new Error(errorMessage);
    }
  }

  async deleteAirstripDistance(fromCode: string, toCode: string): Promise<void> {
    try {
      await api.delete(`/rates/airstrip-distances/${fromCode}/${toCode}`);
    } catch (error: any) {
      console.error('Failed to delete airstrip distance:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete airstrip distance';
      throw new Error(errorMessage);
    }
  }

  // ============================================================================
  // ROUTE CRUD OPERATIONS
  // ============================================================================

  /**
   * Retrieve all scheduled routes
   */
  async getAllRoutes(): Promise<ScheduledRoute[]> {
    try {
      const response = await api.get('/rates/routes');
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to fetch routes:', error);
      return [];
    }
  }

  /**
   * Get a route by ID
   */
  async getRouteById(id: string): Promise<ScheduledRoute | null> {
    try {
      const response = await api.get(`/rates/routes/${id}`);
      return response.data?.data || null;
    } catch (error) {
      console.error(`Failed to fetch route ${id}:`, error);
      return null;
    }
  }

  /**
   * Create a new scheduled route
   */
  async createRoute(
    route: Omit<ScheduledRoute, 'id'>,
    userId: string,
    userName?: string
  ): Promise<ScheduledRoute> {
    try {
      const response = await api.post('/rates/routes', route);
      return response.data?.data;
    } catch (error: any) {
      console.error('Failed to create route:', error);
      throw new Error(error.response?.data?.message || 'Failed to create route');
    }
  }

  /**
   * Bulk update routes (for seasonal demand management)
   * Allows planners to disable specific sectors during low season
   */
  async bulkUpdateRoutes(
    updates: Array<{ id: string; updates: Partial<ScheduledRoute> }>,
    userId: string,
    userName?: string
  ): Promise<ScheduledRoute[]> {
    try {
      const response = await api.patch('/rates/routes/bulk', {
        updates,
        userId,
        userName,
      });
      return response.data?.data || [];
    } catch (error: any) {
      console.error('Failed to bulk update routes:', error);
      throw new Error(error.response?.data?.message || 'Failed to bulk update routes');
    }
  }

  async importRoutes(
    rows: Array<{
      from: string;
      to: string;
      rackRateUSD: number;
      rackChildUSD?: number;
      basicAdultUSD?: number;
      basicChildUSD?: number;
      taxesUSD?: number;
      notes?: string;
      departureTime?: string;
      arrivalTime?: string;
      daysOfWeek?: number[];
      isActive?: boolean;
      blockTimeMinutes?: number;
      effectiveDate?: string;
    }>
  ): Promise<{
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  }> {
    try {
      const response = await api.post('/rates/routes/import', { rows });
      return response.data?.summary || { created: 0, updated: 0, skipped: 0, processed: 0, errors: [] };
    } catch (error: any) {
      console.error('Failed to import routes:', error);
      throw new Error(error.response?.data?.message || 'Failed to import routes');
    }
  }

  /**
   * Get active routes only
   */
  async getActiveRoutes(): Promise<ScheduledRoute[]> {
    try {
      const routes = await this.getAllRoutes();
      return routes.filter(r => r.isActive !== false);
    } catch (error) {
      console.error('Failed to fetch active routes:', error);
      return [];
    }
  }

  /**
   * Toggle route active status (for seasonal management)
   */
  async toggleRouteStatus(
    id: string,
    isActive: boolean,
    userId: string,
    userName?: string
  ): Promise<ScheduledRoute> {
    try {
      const response = await api.patch(`/rates/routes/${id}`, { isActive });
      return response.data?.data;
    } catch (error: any) {
      console.error(`Failed to toggle route ${id}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to toggle route status');
    }
  }

  /**
   * Update an existing route with audit trail
   */
  async updateRoute(
    id: string,
    updates: Partial<ScheduledRoute>,
    userId?: string,
    userName?: string
  ): Promise<ScheduledRoute> {
    try {
      const response = await api.patch(`/rates/routes/${id}`, updates);
      return response.data?.data;
    } catch (error: any) {
      console.error(`Failed to update route ${id}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to update route');
    }
  }

  /**
   * Retrieve aircraft pricing for a route
   */
  async getRouteAircraftRates(routeId: string) {
    try {
      const response = await api.get(`/rates/routes/${routeId}/aircraft-rates`);
      return response.data?.data || [];
    } catch (error) {
      console.error(`Failed to fetch route aircraft rates for ${routeId}:`, error);
      return [];
    }
  }

  /**
   * Upsert a route aircraft pricing record
   */
  async upsertRouteAircraftRate(
    routeId: string,
    payload: { aircraftId: string; ratePerNmUSD: number; ratePerHourUSD: number; isActive?: boolean }
  ) {
    try {
      const response = await api.post(`/rates/routes/${routeId}/aircraft-rates`, payload);
      return response.data?.data;
    } catch (error: any) {
      console.error(`Failed to update route aircraft rate for ${routeId}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to update route aircraft rate');
    }
  }

  /**
   * Delete a route
   */
  async deleteRoute(id: string, userId: string, userName?: string): Promise<void> {
    try {
      await api.delete(`/rates/routes/${id}`);
    } catch (error: any) {
      console.error(`Failed to delete route ${id}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to delete route');
    }
  }

  async deleteAllRoutes(): Promise<{ count: number }> {
    try {
      const response = await api.delete('/rates/routes');
      return { count: Number(response.data?.count || 0) };
    } catch (error: any) {
      console.error('Failed to delete all routes:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete all routes');
    }
  }

  // ============================================================================
  // COMMERCIAL CALCULATION ENGINE
  // ============================================================================

  /**
   * High-fidelity financial synthesis for a flight sector.
   * Merges port fees, passenger taxes, and ancillary surcharges with FX conversion.
   * 
   * @param leg - The flight leg to calculate costs for
   * @param settings - Admin settings containing FX rates and currency preferences
   * @returns Complete cost breakdown in USD and TZS, or null if airstrip not found
   */
  async calculateSectorCommercials(
    leg: Leg,
    settings: AdminSettings
  ): Promise<SectorCommercials | null> {
    const destinationCode = String(leg?.to || '').trim();
    if (!destinationCode) {
      return null;
    }
    try {
      const response = await api.post('/rates/calculate', {
        leg,
        settings,
      });
      return response.data?.data || null;
    } catch (error: any) {
      console.error('Failed to calculate sector commercials:', error);
      // Fallback to local calculation if API fails
      return this.calculateSectorCommercialsLocal(leg, settings);
    }
  }

  /**
   * Local fallback calculation (used when API is unavailable)
   */
  private async calculateSectorCommercialsLocal(
    leg: Leg,
    settings: AdminSettings
  ): Promise<SectorCommercials | null> {
    try {
      const destinationCode = String(leg?.to || '').trim();
      if (!destinationCode) return null;
      const arrivalAirstrip = await this.getAirstripByCode(destinationCode);
      if (!arrivalAirstrip) {
        return null;
      }
      const departureCode = String(leg?.from || '').trim();
      const departureAirstrip = departureCode ? await this.getAirstripByCode(departureCode) : null;

      // Base port fees
      const portFees = {
        landing: arrivalAirstrip.landingFee || 0,
        handling:
          departureAirstrip?.country &&
          arrivalAirstrip?.country &&
          departureAirstrip.country !== arrivalAirstrip.country
            ? ((arrivalAirstrip as any).internationalHandlingFee || arrivalAirstrip.handlingFee || 0)
            : (arrivalAirstrip.handlingFee || 0),
        navigation: arrivalAirstrip.navigationFee || 0,
      };

      // Passenger taxes
      const paxCount = leg.paxCount || 0;
      const taxes = paxCount * (departureAirstrip?.paxTax || 0);

      // Ancillary services
      const ancillaries = {
        meetAndAssist: leg.meetAndAssistActive
          ? paxCount * (arrivalAirstrip.meetAndAssistFee || 0)
          : 0,
        lounge: leg.loungeActive ? paxCount * (arrivalAirstrip.loungeFee || 0) : 0,
      };

      // Calculate total in USD
      const totalUSD =
        portFees.landing +
        portFees.handling +
        portFees.navigation +
        taxes +
        ancillaries.meetAndAssist +
        ancillaries.lounge;

      // FX conversion
      const fxRate = settings.fxRateUSD_TZS || 2300; // Default TZS rate
      const totalTZS = totalUSD * fxRate;

      return {
        usd: Number(totalUSD.toFixed(2)),
        tzs: Number(totalTZS.toFixed(2)),
        breakdown: {
          portFees,
          taxes,
          ancillaries,
        },
        currency: settings.defaultCurrency || 'USD',
        fxRate,
        fxTimestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Local calculation failed:', error);
      return null;
    }
  }

  // ============================================================================
  // AUDIT TRAIL ACCESS
  // ============================================================================

  /**
   * Get all audit logs for rates changes
   */
  async getAuditLogs(limit?: number): Promise<RateAudit[]> {
    try {
      const params = limit ? { limit: limit.toString() } : {};
      const response = await api.get('/rates/audits', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs for a specific target (airstrip or route)
   */
  async getAuditLogsForTarget(
    targetId: string,
    targetType?: 'AIRSTRIP' | 'ROUTE'
  ): Promise<RateAudit[]> {
    try {
      const params: any = { targetId };
      if (targetType) params.targetType = targetType;
      const response = await api.get('/rates/audits', { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Failed to fetch audit logs for target:', error);
      return [];
    }
  }
}

export const ratesService = new RatesService();
